<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="goBack">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">{{ product?.name || 'Détails du produit' }}</h1>
          <p class="text-xs text-gray-400">{{ product?.reference ? `Réf: ${product.reference}` : 'Consultez les informations du produit' }}</p>
        </div>
      </div>
      <div v-if="product" class="flex items-center gap-2">
        <button v-permission="'products.edit'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="editProduct">
          <i class="fas fa-pen text-[11px]"></i>
          Modifier
        </button>
        <button v-permission="'products.delete'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          @click="confirmDelete">
          <i class="fas fa-trash text-[11px]"></i>
          Supprimer
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading"
      class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement des détails du produit...</p>
    </div>

    <!-- Erreur -->
    <div v-else-if="error"
      class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-circle-exclamation text-4xl"></i>
      </div>
      <h3 class="text-base font-semibold text-gray-700 mb-1">Une erreur est survenue</h3>
      <p class="text-sm text-gray-400 max-w-sm mx-auto mb-6">{{ error }}</p>
      <button
        class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="loadProductDetails">
        Réessayer
      </button>
    </div>

    <!-- Contenu principal -->
    <div v-else-if="product" class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte produit (image + infos principales) -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="flex flex-col md:flex-row">
            <!-- Image -->
            <div class="md:w-64 lg:w-72 flex-shrink-0 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-100 flex items-center justify-center p-5">
              <img v-if="product.image_url" :src="product.image_url" :alt="product.name"
                class="max-w-full max-h-56 object-contain rounded-lg" />
              <div v-else class="text-center py-10">
                <i class="fas fa-image text-gray-300 text-4xl mb-2"></i>
                <p class="text-xs text-gray-400">Aucune image</p>
              </div>
            </div>

            <!-- Infos principales -->
            <div class="flex-1 p-5">
              <div class="flex items-start justify-between mb-4">
                <div>
                  <h2 class="text-base font-bold text-gray-900 uppercase">{{ product.name }}</h2>
                  <div class="flex flex-wrap gap-1.5 mt-2">
                    <span v-if="getCategoryName(product.category_id)"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                      {{ getCategoryName(product.category_id) }}
                    </span>
                    <span :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      product.stock_quantity == null || product.stock_quantity <= 0
                        ? 'bg-red-50 text-red-600'
                        : product.stock_quantity <= 5
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-blue-50 text-blue-700'
                    ]">
                      {{ product.stock_quantity == null || product.stock_quantity <= 0
                        ? 'Rupture de stock'
                        : product.stock_quantity <= 5
                          ? `Stock faible: ${product.stock_quantity}`
                          : `En stock: ${product.stock_quantity}` }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="product.description" class="mb-4">
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Description</p>
                <p class="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{{ product.description }}</p>
              </div>

              <!-- Grille de détails -->
              <div class="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                <div v-if="product.sku">
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">SKU</p>
                  <p class="text-sm font-medium text-gray-900 font-mono">{{ product.sku }}</p>
                </div>
                <div v-if="product.barcode">
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Code-barres</p>
                  <p class="text-sm font-medium text-gray-900 font-mono">{{ product.barcode }}</p>
                </div>
                <div v-if="product.reference">
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Référence</p>
                  <p class="text-sm font-medium text-gray-900 font-mono">{{ product.reference }}</p>
                </div>
                <div v-if="product.unit">
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Unité</p>
                  <p class="text-sm font-medium text-gray-900">{{ product.unit }}</p>
                </div>
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Créé le</p>
                  <p class="text-sm font-medium text-gray-700">{{ formatDate(product.created_at) }}</p>
                </div>
                <div>
                  <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Mis à jour</p>
                  <p class="text-sm font-medium text-gray-700">{{ formatDate(product.updated_at) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Historique des ventes -->
        <div class="bg-white rounded-xl border border-gray-200">
          <ProductSalesHistory :product-id="product?.id" :limit="10" />
        </div>

        <!-- Mouvements de stock -->
        <div v-if="product && product.track_inventory" class="bg-white rounded-xl border border-gray-200">
          <ProductStockTransactions :product-id="product?.id" :per-page="10" />
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Prix -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b bg-primary/5 border-primary/10">
            <h3 class="text-sm font-semibold text-primary">Prix de vente</h3>
          </div>
          <div class="p-5">
            <div class="text-center mb-4">
              <p class="text-2xl font-bold text-gray-900">{{ formatPrice(product.price_incl_tax) }}</p>
              <p class="text-xs text-gray-400 mt-0.5">TTC</p>
            </div>
            <div class="space-y-2.5 pt-3 border-t border-gray-100">
              <div class="flex justify-between items-center">
                <span class="text-xs text-gray-400">Prix HT</span>
                <span class="text-sm font-semibold text-gray-900">{{ formatPrice(product.price_excl_tax) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-xs text-gray-400">Taux TVA</span>
                <span class="text-sm font-medium text-gray-900">{{ product.tax_rate || 0 }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Stock -->
        <div v-if="product.track_inventory" class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Gestion du stock</h3>
          </div>
          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="getStockStatusInfo().bgClass">
                  <i :class="getStockStatusInfo().iconClass" class="text-[10px]"></i>
                </span>
                Quantité actuelle
              </span>
              <div class="text-right">
                <span class="text-sm font-bold text-gray-900">{{ product.stock_quantity || 0 }} {{ product.unit || '' }}</span>
                <p class="text-[10px] font-medium" :class="getStockStatusInfo().textClass">
                  {{ getStockStatusInfo().label }}
                </p>
              </div>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-arrow-down text-gray-400 text-[10px]"></i>
                </span>
                Stock minimum
              </span>
              <span class="text-sm font-medium text-gray-900">{{ product.minimum_stock || 0 }} {{ product.unit || '' }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-arrow-up text-gray-400 text-[10px]"></i>
                </span>
                Stock maximum
              </span>
              <span class="text-sm font-medium text-gray-900">{{ product.maximum_stock || '-' }} {{ product.unit || '' }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-coins text-green-500 text-[10px]"></i>
                </span>
                Valeur du stock
              </span>
              <span class="text-sm font-bold text-green-600">{{ formatPrice((product.stock_quantity || 0) * (product.price_excl_tax || 0)) }}</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
          <div class="space-y-1.5">
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="editProduct">
              <i class="fas fa-pen text-xs w-4 text-center"></i>
              Modifier le produit
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="goToProductDetails">
              <i class="fas fa-print text-xs w-4 text-center"></i>
              Imprimer la fiche
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              @click="confirmDelete">
              <i class="fas fa-trash text-xs w-4 text-center"></i>
              Supprimer le produit
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de suppression -->
    <Teleport to="body">
      <transition name="modal">
        <div v-if="showDeleteModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="showDeleteModal = false">
          <div class="bg-white rounded-xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div class="w-14 h-14 rounded-full bg-red-50 mx-auto mb-4 flex items-center justify-center">
              <i class="fas fa-trash text-red-500 text-xl"></i>
            </div>
            <h3 class="text-base font-bold text-gray-900 mb-2">Supprimer le produit ?</h3>
            <p class="text-sm text-gray-500 mb-6 leading-relaxed">
              Êtes-vous sûr de vouloir supprimer <strong>{{ product?.name }}</strong> ?
              Cette action est irréversible.
            </p>
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                :disabled="deleteLoading"
                @click="showDeleteModal = false">
                Annuler
              </button>
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
                :disabled="deleteLoading"
                @click="deleteProduct">
                <i v-if="deleteLoading" class="fas fa-spinner fa-spin text-xs mr-1.5"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Modal de modification -->
    <EditProductModal v-if="showEditModal" :show="showEditModal" :product="product" :categories="categories"
      @close="showEditModal = false" @submit="handleProductUpdate" />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { productsService, categoriesService, inventoryTransactionService } from '@/services'
import { useToastStore } from '@/store/modules'
import EditProductModal from '@/views/products/EditProductModal.vue'
import ProductSalesHistory from '@/components/products/ProductSalesHistory.vue'
import ProductStockTransactions from '@/components/products/ProductStockTransactions.vue'

export default {
  name: 'ProductDetailView',
  components: {
    EditProductModal,
    ProductSalesHistory,
    ProductStockTransactions
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toastStore = useToastStore()

    const product = ref(null)
    const productSales = ref([])
    const productTransactions = ref([])
    const categories = ref([])

    const loading = ref(true)
    const error = ref(null)
    const showDeleteModal = ref(false)
    const showEditModal = ref(false)
    const deleteLoading = ref(false)
    const transactionsLoading = ref(false)
    const transactionsPagination = ref({
      currentPage: 1,
      totalPages: 1,
      total: 0,
      perPage: 10
    })

    const productId = computed(() => route.params.id)

    const loadProductDetails = async () => {
      loading.value = true
      error.value = null

      try {
        const response = await productsService.getProduct(productId.value, {
          "with[]": ['category', 'media']
        })
        product.value = response.data.product || response.data

        if (categories.value.length === 0) {
          await fetchCategories()
        }
      } catch (err) {
        console.error("Erreur lors du chargement du produit:", err)
        error.value = "Impossible de charger les détails du produit. Veuillez réessayer."
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement des détails du produit.',
          duration: 5000
        })
      } finally {
        loading.value = false
      }
    }

    const loadProductSales = async () => {
      try {
        const response = await productsService.getProductSales(productId.value)
        if (response && response.data) {
          productSales.value = response.data.data
        }
      } catch (err) {
        console.error("Erreur lors du chargement des ventes:", err)
      }
    }

    const fetchCategories = async () => {
      try {
        const response = await categoriesService.getCategories({ perPage: 100, page: 1 })
        if (response && response.data) {
          categories.value = response.data.data || response.data
        }
      } catch (err) {
        console.error("Erreur lors du chargement des catégories:", err)
      }
    }

    const loadProductTransactions = async (page = 1) => {
      transactionsLoading.value = true
      try {
        const response = await inventoryTransactionService.getTransactions({
          product_id: productId.value,
          per_page: transactionsPagination.value.perPage,
          page: page
        })

        if (response && response.data) {
          if (response.data.data) {
            productTransactions.value = response.data.data
            transactionsPagination.value = {
              currentPage: response.data.current_page || page,
              totalPages: response.data.last_page || 1,
              total: response.data.total || 0,
              perPage: response.data.per_page || 20
            }
          } else {
            productTransactions.value = response.data
          }
        }
      } catch (err) {
        console.error("Erreur lors du chargement des transactions:", err)
      } finally {
        transactionsLoading.value = false
      }
    }

    const goBack = () => {
      router.push({ name: 'products' })
    }

    const editProduct = () => {
      showEditModal.value = true
    }

    const goToProductDetails = () => {
      // Placeholder for print functionality
    }

    const handleProductUpdate = async (formData) => {
      try {
        const id = product.value.id
        const data = new FormData()

        Object.keys(formData).forEach(key => {
          if (key !== 'image' && key !== 'id' && key !== 'image_url' && key !== 'remove_existing_image' && formData[key] !== null && formData[key] !== undefined) {
            data.append(key, formData[key])
          }
        })

        if (formData.image && formData.image instanceof File) {
          data.append('image', formData.image)
        } else if (formData.remove_existing_image) {
          data.append('remove_image', '1')
        }

        await productsService.updateProduct(id, data)
        showEditModal.value = false
        toastStore.addToast({ type: 'success', message: 'Produit mis à jour avec succès !' })
        await loadProductDetails()
      } catch (err) {
        console.error("Erreur lors de la mise à jour du produit:", err)
        toastStore.addToast({ type: 'error', message: 'Erreur lors de la mise à jour du produit' })
      }
    }

    const confirmDelete = () => {
      showDeleteModal.value = true
    }

    const deleteProduct = async () => {
      deleteLoading.value = true
      try {
        await productsService.deleteProduct(product.value.id)
        showDeleteModal.value = false
        toastStore.addToast({ type: 'success', message: 'Produit supprimé avec succès !' })
        router.push({ name: 'products' })
      } catch (err) {
        console.error("Erreur lors de la suppression du produit:", err)
        toastStore.addToast({ type: 'error', message: 'Erreur lors de la suppression du produit' })
      } finally {
        deleteLoading.value = false
      }
    }

    const formatPrice = (price) => {
      if (!price && price !== 0) return '-'
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(price)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const getCategoryName = (categoryId) => {
      if (!categoryId) return ''
      const category = categories.value.find(cat => cat.id === categoryId)
      return category ? category.name : ''
    }

    const getStockStatusInfo = () => {
      if (!product.value || !product.value.track_inventory) {
        return { label: 'Non suivi', bgClass: 'bg-gray-50', iconClass: 'fas fa-minus text-gray-400', textClass: 'text-gray-400' }
      }
      const stock = product.value.stock_quantity || 0
      const min = product.value.minimum_stock || 0

      if (stock <= 0) {
        return { label: 'Rupture de stock', bgClass: 'bg-red-50', iconClass: 'fas fa-times text-red-500', textClass: 'text-red-500' }
      } else if (stock <= min) {
        return { label: 'Stock faible', bgClass: 'bg-amber-50', iconClass: 'fas fa-exclamation text-amber-500', textClass: 'text-amber-500' }
      } else {
        return { label: 'En stock', bgClass: 'bg-green-50', iconClass: 'fas fa-check text-green-500', textClass: 'text-green-500' }
      }
    }

    onMounted(() => {
      loadProductDetails()
      loadProductSales()
      loadProductTransactions()
    })

    return {
      product,
      productSales,
      productTransactions,
      categories,
      loading,
      error,
      showDeleteModal,
      showEditModal,
      deleteLoading,
      transactionsLoading,
      transactionsPagination,
      loadProductDetails,
      loadProductTransactions,
      goBack,
      editProduct,
      goToProductDetails,
      handleProductUpdate,
      confirmDelete,
      deleteProduct,
      formatPrice,
      formatDate,
      getCategoryName,
      getStockStatusInfo
    }
  }
}
</script>

<style scoped>
.modal-enter-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active > div {
  transition: transform 0.2s ease;
}
.modal-leave-active {
  transition: opacity 0.15s ease;
}
.modal-leave-active > div {
  transition: transform 0.15s ease;
}
.modal-enter-from {
  opacity: 0;
}
.modal-enter-from > div {
  transform: scale(0.95) translateY(-8px);
}
.modal-leave-to {
  opacity: 0;
}
.modal-leave-to > div {
  transform: scale(0.95) translateY(-8px);
}
</style>
